import React from 'react';
import {
  string,
  number,
  bool,
  func,
  arrayOf,
  shape,
  element,
  oneOfType,
} from 'prop-types';
import {
  Checkbox, FormGroup, SelectionControlLabel, Radio, RadioGroup, Typography
} from '@one-thd/sui-atomic-components';
import { ErrorFilled } from '@one-thd/sui-icons';
import './product-add-ons-pod.scss';

const ProductAddOnsPodStencil = ({
  heading,
  subHeading,
  userOptions,
  icon,
  onChange,
  tooltip,
  tooltipOptional,
  displayDisabledText,
  radio,
  brandText,
  contactInfo,
  children,
}) => {
  const Input = radio ? Radio : Checkbox;
  const Group = radio ? RadioGroup : FormGroup;
  const handleChange = (evt) => {
    onChange(evt);
  };
  const getDisplayPrice = (price) => {
    if (price === 0) {
      return <span className="sui-font-bold sui-text-success">&nbsp;FREE</span>;
    }
    if (!price) {
      return null;
    }
    if (typeof price === 'number') {
      return (
        <span className="sui-font-bold">&nbsp;+&#36;{price.toFixed(2)}</span>
      );
    }
    if (typeof price === 'string') {
      return (
        <span className="sui-font-bold">{price}</span>
      );
    }
    return null;
  };
  if (displayDisabledText) {
    return (
      <div className="sui-flex sui-py-5 sui-text-danger">
        <div className="sui-mt-1"><ErrorFilled size="small" /></div>
        <div className="sui-ml-2">
          {displayDisabledText}
        </div>
      </div>
    );
  }
  return (
    <div className="sui-flex sui-flex-col sui-pl-1">
      {brandText && <div className="sui-mb-4 sui-text-brand">Leave Your Next Project To Us</div>}
      <div className="sui-flex sui-mb-5">
        <div className="sui-mr-3">{icon}</div>
        <div className="sui-leading-loose">
          <Typography variant="h4" weight="display">{heading}</Typography>
          <div data-testid="subHeading" className="sui-text-base sui-text-subtle"> {subHeading} </div>
          {userOptions.length > 0 && (
            <div className="sui-flex">
              <Group>
                {
                  (userOptions || []).map(
                    ({
                      value, checked, label, price
                    }, id) => (
                      <SelectionControlLabel
                        label={<div data-id={id}>{label}{getDisplayPrice(price)}</div>}
                        value={value}
                      >
                        <Input id={`${value}__checkbox`} checked={checked} onChange={handleChange} />
                      </SelectionControlLabel>
                    ))
                }
              </Group>
              {tooltipOptional}
            </div>
          )}
          {contactInfo}
          {tooltip}
        </div>
      </div>
      {children}
    </div>
  );
};

ProductAddOnsPodStencil.displayName = 'ProductAddOns-ProductAddOnsPodStencil';

ProductAddOnsPodStencil.propTypes = {
  /** heading -> Renders as the descriptive title */
  heading: string.isRequired,
  /** subHeading -> Renders as the descriptive sub-title */
  subHeading: oneOfType([element, string]),
  /** icon -> Renders as background image */
  icon: string,
  /** onChange -> callback ref for user selection event */
  onChange: func,
  /** Populates the tooltip */
  tooltip: element,
  /** Populates the optional tooltip */
  tooltipOptional: element,
  /** Populates the contact information */
  contactInfo: element,
  /** userOptions -> list that display input type of checkbox or radio and its label */
  userOptions: arrayOf(shape({
    /** value - current product add on */
    value: string.isRequired,
    /** checkbox state */
    checked: bool.isRequired,
    /** Checkbox Label */
    label: string.isRequired,
    /** Price of the add on */
    price: oneOfType([number, string]),
  })),
  /** radio -> Checkbox is the default type for user options list.  */
  radio: bool,
  /** disabledDisplayText - When this is truthy, display this over default template */
  displayDisabledText: string,
  /** Displays brand text */
  brandText: bool,
  /** Any element that will be rendered below the existing template */
  children: element,
};

ProductAddOnsPodStencil.defaultProps = {
  brandText: true,
  subHeading: '',
  displayDisabledText: '',
  radio: false,
  userOptions: [],
  tooltip: null,
  tooltipOptional: null,
  icon: '',
  contactInfo: null,
  children: null,
  onChange: () => { },
};

export { ProductAddOnsPodStencil };
